import { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useHasPermission, useIsTenantAdmin } from '@/hooks/usePermissions';
import { useUnreadNotificationCount } from '@/hooks/useNotificationPolling';
import { useNavPins } from '@/hooks/useNavPins';
import { useNavUsage } from '@/hooks/useNavUsage';
import { useNavSections } from '@/hooks/useNavSections';
import { NavSearch } from './NavSearch';
import { CollapsibleSection } from './CollapsibleSection';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { Permissions, RolePermissions } from '@jerico/shared';
import type { Permission, UserRole } from '@jerico/shared';

type AppMode = 'practice' | 'grow';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  permission?: Permission;
  requireTenantAdmin?: boolean;
}

interface SmartNavSection {
  id: string;
  label: string;
  mode: AppMode | 'both';
  coreCount: number; // items visible before "Show more"
  items: NavItem[];
}

// ─── Reorganized into fewer, focused sections ────────────────────────────────

const smartNavSections: SmartNavSection[] = [
  {
    id: 'work',
    label: 'Work',
    mode: 'practice',
    coreCount: 8,
    items: [
      { to: '/app', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
      { to: '/app/matters', label: 'Matters', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', permission: Permissions.VIEW_CLIENTS },
      { to: '/app/clients', label: 'Clients', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', permission: Permissions.VIEW_CLIENTS },
      { to: '/app/contacts', label: 'Contacts', icon: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z', permission: Permissions.VIEW_CONTACTS },
      { to: '/app/tasks', label: 'Tasks', icon: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permission: Permissions.VIEW_MATTERS },
      { to: '/app/calendar', label: 'Calendar', icon: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5', permission: Permissions.VIEW_MATTERS },
      { to: '/app/timesheet', label: 'Timesheet', icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z', permission: Permissions.VIEW_MATTERS },
      { to: '/app/deadlines', label: 'Deadlines', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', permission: Permissions.VIEW_CLIENTS },
      // Show more
      { to: '/app/organizations', label: 'Organizations', icon: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21', permission: Permissions.VIEW_ORGANIZATIONS },
      { to: '/app/court-deadlines', label: 'Court Deadlines', icon: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008z', permission: Permissions.MANAGE_MATTERS },
      { to: '/app/conflicts', label: 'Conflict Search', icon: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z', permission: Permissions.VIEW_CLIENTS },
      { to: '/app/kanban', label: 'Kanban Board', icon: 'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z', permission: Permissions.VIEW_MATTERS },
      { to: '/app/approvals', label: 'Approvals', icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z', permission: Permissions.MANAGE_MATTERS },
      { to: '/app/activity', label: 'Activity Feed', icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z', permission: Permissions.VIEW_MATTERS },
    ],
  },
  {
    id: 'docs',
    label: 'Documents',
    mode: 'practice',
    coreCount: 3,
    items: [
      { to: '/app/documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', permission: Permissions.VIEW_DOCUMENTS },
      { to: '/app/legal-research', label: 'Legal Research', icon: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z', permission: Permissions.MANAGE_MATTERS },
      { to: '/app/knowledge-base', label: 'Knowledge Base', icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25', permission: Permissions.VIEW_MATTERS },
      // Show more
      { to: '/app/signatures', label: 'Signatures', icon: 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10', permission: Permissions.MANAGE_MATTERS },
      { to: '/app/docgen', label: 'Doc Generation', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z', permission: Permissions.MANAGE_MATTERS },
      { to: '/app/contract-review', label: 'Contract Review', icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z', permission: Permissions.MANAGE_MATTERS },
      { to: '/app/transcription', label: 'Transcription', icon: 'M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z', permission: Permissions.VIEW_MATTERS },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    mode: 'practice',
    coreCount: 3,
    items: [
      { to: '/app/invoices', label: 'Invoices', icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z', permission: Permissions.VIEW_BILLING },
      { to: '/app/expenses', label: 'Expenses', icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z', permission: Permissions.VIEW_BILLING },
      { to: '/app/trust', label: 'Trust / IOLTA', icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z', permission: Permissions.VIEW_BILLING },
      // Show more
      { to: '/app/billing/rate-card', label: 'Rate Card', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permission: Permissions.VIEW_BILLING },
      { to: '/app/billing/payment-plans', label: 'Payment Plans', icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z', permission: Permissions.VIEW_BILLING },
      { to: '/app/reports', label: 'Reports', icon: 'M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5', permission: Permissions.VIEW_BILLING },
      { to: '/app/analytics/revenue', label: 'Revenue Analytics', icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z', permission: Permissions.VIEW_BILLING },
    ],
  },
  {
    id: 'comms',
    label: 'Communication',
    mode: 'both',
    coreCount: 4,
    items: [
      { to: '/app/inbox', label: 'Unified Inbox', icon: 'M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-17.5 0V6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v6.75m-19.5 0v4.5A2.25 2.25 0 004.5 19.5h15a2.25 2.25 0 002.25-2.25v-4.5', permission: Permissions.VIEW_MESSAGES },
      { to: '/app/emails', label: 'Email', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', permission: Permissions.VIEW_MESSAGES },
      { to: '/app/conversations', label: 'Conversations', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', permission: Permissions.VIEW_MESSAGES },
      { to: '/app/portal', label: 'Client Portal', icon: 'M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z', permission: Permissions.VIEW_CLIENTS },
      // Show more
      { to: '/app/sms', label: 'SMS', icon: 'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3', permission: Permissions.VIEW_CLIENTS },
      { to: '/app/notifications', label: 'Notifications', icon: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0', permission: Permissions.VIEW_CLIENTS },
      { to: '/app/communications', label: 'Comm Log', icon: 'M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155', permission: Permissions.VIEW_CLIENTS },
    ],
  },
  {
    id: 'ai',
    label: 'AI Tools',
    mode: 'practice',
    coreCount: 2,
    items: [
      { to: '/app/ai-chat', label: 'AI Assistant', icon: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z', permission: Permissions.VIEW_CLIENTS },
      { to: '/app/workflows', label: 'Workflows', icon: 'M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z', permission: Permissions.MANAGE_BOT },
      // Show more
      { to: '/app/ai-analytics', label: 'AI Analytics', icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z', permission: Permissions.MANAGE_BOT },
      { to: '/app/memory', label: 'AI Memory', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z', permission: Permissions.MANAGE_BOT },
      { to: '/app/compliance', label: 'Compliance', icon: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z', permission: Permissions.MANAGE_MATTERS },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    mode: 'grow',
    coreCount: 5,
    items: [
      { to: '/app/growth', label: 'Growth Center', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6', permission: Permissions.MANAGE_BOT },
      { to: '/app/campaigns', label: 'Campaigns', icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z', permission: Permissions.MANAGE_BOT },
      { to: '/app/channels', label: 'Channels', icon: 'M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2h-2M9 12l2 2 4-4', permission: Permissions.MANAGE_BOT },
      { to: '/app/content-studio', label: 'Content Studio', icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z', permission: Permissions.MANAGE_BOT },
      { to: '/app/marketing-analytics', label: 'Mktg Analytics', icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z', permission: Permissions.MANAGE_BOT },
      // Show more
      { to: '/app/lead-magnets', label: 'Lead Magnets', icon: 'M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z', permission: Permissions.MANAGE_BOT },
      { to: '/app/bot-config', label: 'Bot Config', icon: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75', permission: Permissions.MANAGE_BOT },
      { to: '/app/social', label: 'Social Connect', icon: 'M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z', permission: Permissions.MANAGE_BOT },
      { to: '/app/social-posts', label: 'Social Posts', icon: 'M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z', permission: Permissions.MANAGE_BOT },
      { to: '/app/campaign-social', label: 'Campaign Social', icon: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46', permission: Permissions.MANAGE_BOT },
      { to: '/app/lead-scoring', label: 'Lead Scoring', icon: 'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z', permission: Permissions.MANAGE_BOT },
    ],
  },
];

// ─── Footer nav items (always visible at bottom) ─────────────────────────────

const footerNavItems: NavItem[] = [
  { to: '/app/profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { to: '/app/settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', requireTenantAdmin: true },
  { to: '/app/users', label: 'Team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', permission: Permissions.MANAGE_USERS },
];

// ─── Flat list of all items for search & pins ───────────────────────────────

interface FlatNavItem extends NavItem {
  sectionLabel: string;
  sectionId: string;
}

const allFlatItems: FlatNavItem[] = [
  ...smartNavSections.flatMap((s) =>
    s.items.map((item) => ({ ...item, sectionLabel: s.label, sectionId: s.id }))
  ),
  ...footerNavItems.map((item) => ({ ...item, sectionLabel: 'Account', sectionId: 'footer' })),
];

// ─── Star icon SVG path (Heroicons star) ────────────────────────────────────

const STAR_PATH =
  'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z';

// ─── Nav item components with permission gating ─────────────────────────────

interface SmartNavLinkProps {
  item: NavItem;
  pinned: boolean;
  suggestPin: boolean;
  onTogglePin: () => void;
  onClick: () => void;
  notificationCount: number;
  sectionHint?: string;
}

function SmartNavLink({
  item,
  pinned,
  suggestPin,
  onTogglePin,
  onClick,
  notificationCount,
  sectionHint,
}: SmartNavLinkProps) {
  const isNotifications = item.to === '/app/notifications';

  return (
    <li className="group relative">
      <NavLink
        to={item.to}
        end={item.to === '/app'}
        onClick={onClick}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            isActive
              ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
          }`
        }
      >
        <svg
          className="h-5 w-5 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
        </svg>
        <span className="flex-1 truncate">{item.label}</span>
        {sectionHint && (
          <span className="text-[10px] text-gray-300 dark:text-gray-600 truncate max-w-[60px]">
            {sectionHint}
          </span>
        )}
        {isNotifications && notificationCount > 0 && (
          <span className="inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
            {notificationCount > 99 ? '99+' : notificationCount}
          </span>
        )}
      </NavLink>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTogglePin();
        }}
        className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 transition-all ${
          pinned
            ? 'opacity-50 hover:opacity-100 text-brand-500 dark:text-brand-400'
            : `opacity-0 group-hover:opacity-50 hover:!opacity-100 text-gray-400 dark:text-gray-500 ${
                suggestPin ? '!opacity-60 animate-pulse' : ''
              }`
        }`}
        title={pinned ? 'Unpin from top' : 'Pin to top'}
      >
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          fill={pinned ? 'currentColor' : 'none'}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={STAR_PATH} />
        </svg>
      </button>
    </li>
  );
}

function PermissionGatedNavLink({
  item,
  ...props
}: SmartNavLinkProps & { item: NavItem & { permission: Permission } }) {
  const hasPermission = useHasPermission(item.permission);
  if (!hasPermission) return null;
  return <SmartNavLink item={item} {...props} />;
}

function TenantAdminGatedNavLink({ item, ...props }: SmartNavLinkProps) {
  const isTenantAdmin = useIsTenantAdmin();
  if (!isTenantAdmin) return null;
  return <SmartNavLink item={item} {...props} />;
}

function renderSmartItem(
  item: NavItem,
  pinned: boolean,
  suggestPin: boolean,
  onTogglePin: () => void,
  onClick: () => void,
  notificationCount: number,
  sectionHint?: string,
) {
  const props: SmartNavLinkProps = { item, pinned, suggestPin, onTogglePin, onClick, notificationCount, sectionHint };

  if (item.requireTenantAdmin) {
    return <TenantAdminGatedNavLink key={item.to} {...props} />;
  }
  if (item.permission) {
    return (
      <PermissionGatedNavLink
        key={item.to}
        {...props}
        item={item as NavItem & { permission: Permission }}
      />
    );
  }
  return <SmartNavLink key={item.to} {...props} />;
}

// ─── Sidebar ────────────────────────────────────────────────────────────────

export function Sidebar({ onClose }: { onClose?: () => void }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const unreadCount = useUnreadNotificationCount();

  const [appMode, setAppMode] = useState<AppMode>(() => {
    return (localStorage.getItem('jerico_app_mode') as AppMode) || 'practice';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOverflows, setExpandedOverflows] = useState<Record<string, boolean>>({});

  const { pinnedPaths, isPinned, togglePin } = useNavPins();
  const { recordClick, shouldSuggestPin, dismissSuggestion } = useNavUsage();
  const { isExpanded, toggleSection } = useNavSections();

  // Auto-close sidebar on navigation (mobile)
  useEffect(() => {
    onClose?.();
  }, [location.pathname, onClose]);

  const handleModeChange = (mode: AppMode) => {
    setAppMode(mode);
    localStorage.setItem('jerico_app_mode', mode);
  };

  const toggleOverflow = useCallback((sectionId: string) => {
    setExpandedOverflows((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  // Pre-compute user permissions for counting visible items
  const userPerms = useMemo(() => {
    if (!user) return [] as Permission[];
    return RolePermissions[user.role as UserRole] || [];
  }, [user]);

  const isTenantAdminUser = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'system_admin';

  const isItemVisible = useCallback(
    (item: NavItem) => {
      if (item.requireTenantAdmin) return isTenantAdminUser;
      if (item.permission) return userPerms.includes(item.permission);
      return true;
    },
    [userPerms, isTenantAdminUser],
  );

  // Sections filtered by current app mode
  const visibleSections = useMemo(() => {
    return smartNavSections.filter((s) => s.mode === 'both' || s.mode === appMode);
  }, [appMode]);

  // Search results (search across ALL sections regardless of mode)
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allFlatItems.filter(
      (item) => item.label.toLowerCase().includes(q) && isItemVisible(item),
    );
  }, [searchQuery, isItemVisible]);

  // Pinned items (resolve paths to full item objects)
  const pinnedItems = useMemo(() => {
    return pinnedPaths
      .map((path) => allFlatItems.find((item) => item.to === path))
      .filter((item): item is FlatNavItem => item != null && isItemVisible(item));
  }, [pinnedPaths, isItemVisible]);

  const handleNavClick = useCallback(
    (path: string) => {
      recordClick(path);
    },
    [recordClick],
  );

  const handleTogglePin = useCallback(
    (path: string) => {
      togglePin(path);
      dismissSuggestion(path);
    },
    [togglePin, dismissSuggestion],
  );

  const isSearching = searchQuery.trim().length > 0;

  return (
    <aside className="flex h-screen w-64 flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      {/* Header */}
      <div className="relative border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute right-3 top-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 md:hidden"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <h1 className="text-xl font-bold text-brand-700 dark:text-brand-400">Jerico</h1>
      </div>

      {/* Search + Mode toggle */}
      <div className="px-3 pt-3 pb-1 space-y-2">
        <NavSearch query={searchQuery} onQueryChange={setSearchQuery} />
        <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
          <button
            onClick={() => handleModeChange('practice')}
            className={`flex-1 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              appMode === 'practice'
                ? 'bg-white text-brand-700 shadow-sm dark:bg-gray-700 dark:text-brand-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Practice
          </button>
          <button
            onClick={() => handleModeChange('grow')}
            className={`flex-1 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              appMode === 'grow'
                ? 'bg-white text-brand-700 shadow-sm dark:bg-gray-700 dark:text-brand-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Grow
          </button>
        </div>
      </div>

      {/* Scrollable nav area */}
      <nav className="flex-1 overflow-y-auto px-3 pb-2">
        {isSearching ? (
          /* ─── Search Results ────────────────────────────────────── */
          <div>
            {searchResults.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
                No results for &ldquo;{searchQuery}&rdquo;
              </p>
            ) : (
              <ul className="space-y-0.5">
                {searchResults.map((item) =>
                  renderSmartItem(
                    item,
                    isPinned(item.to),
                    shouldSuggestPin(item.to, isPinned(item.to)),
                    () => handleTogglePin(item.to),
                    () => handleNavClick(item.to),
                    unreadCount,
                    item.sectionLabel,
                  ),
                )}
              </ul>
            )}
          </div>
        ) : (
          <>
            {/* ─── Pinned Section ──────────────────────────────────── */}
            {pinnedItems.length > 0 && (
              <div className="mb-2">
                <div className="mb-1 flex items-center gap-1.5 px-3 py-1">
                  <svg
                    className="h-3 w-3 text-brand-500 dark:text-brand-400"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={STAR_PATH} />
                  </svg>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    Pinned
                  </span>
                </div>
                <ul className="space-y-0.5">
                  {pinnedItems.map((item) =>
                    renderSmartItem(
                      item,
                      true,
                      false,
                      () => handleTogglePin(item.to),
                      () => handleNavClick(item.to),
                      unreadCount,
                    ),
                  )}
                </ul>
              </div>
            )}

            {/* ─── Divider ─────────────────────────────────────────── */}
            {pinnedItems.length > 0 && (
              <div className="my-2 border-t border-gray-100 dark:border-gray-800" />
            )}

            {/* ─── Collapsible Sections with overflow ──────────────── */}
            {visibleSections.map((section) => {
              const visibleItems = section.items.filter(isItemVisible);
              if (visibleItems.length === 0) return null;

              const coreItems = visibleItems.slice(0, section.coreCount);
              const overflowItems = visibleItems.slice(section.coreCount);
              const isOverflowOpen = !!expandedOverflows[section.id];

              return (
                <CollapsibleSection
                  key={section.id}
                  id={section.id}
                  label={section.label}
                  itemCount={coreItems.length}
                  isExpanded={isExpanded(section.id)}
                  onToggle={() => toggleSection(section.id)}
                >
                  {coreItems.map((item) =>
                    renderSmartItem(
                      item,
                      isPinned(item.to),
                      shouldSuggestPin(item.to, isPinned(item.to)),
                      () => handleTogglePin(item.to),
                      () => handleNavClick(item.to),
                      unreadCount,
                    ),
                  )}
                  {overflowItems.length > 0 && (
                    <>
                      {isOverflowOpen && overflowItems.map((item) =>
                        renderSmartItem(
                          item,
                          isPinned(item.to),
                          shouldSuggestPin(item.to, isPinned(item.to)),
                          () => handleTogglePin(item.to),
                          () => handleNavClick(item.to),
                          unreadCount,
                        ),
                      )}
                      <li>
                        <button
                          onClick={() => toggleOverflow(section.id)}
                          className="flex w-full items-center gap-2 rounded-md px-3 py-1 text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                        >
                          <svg
                            className={`h-3 w-3 transition-transform ${isOverflowOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                          {isOverflowOpen ? 'Show less' : `${overflowItems.length} more...`}
                        </button>
                      </li>
                    </>
                  )}
                </CollapsibleSection>
              );
            })}
          </>
        )}
      </nav>

      {/* Footer — Profile, Settings, Team + user info */}
      <div className="border-t border-gray-200 dark:border-gray-700">
        {/* Footer nav links */}
        <ul className="px-3 py-2 space-y-0.5">
          {footerNavItems.map((item) =>
            renderSmartItem(
              item,
              isPinned(item.to),
              false,
              () => handleTogglePin(item.to),
              () => handleNavClick(item.to),
              unreadCount,
            ),
          )}
        </ul>

        {/* User info */}
        <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="mb-2 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {user?.displayName || user?.email}
          </div>
          <div className="mb-3 flex items-center justify-between">
            <span className="truncate text-xs text-gray-500 dark:text-gray-400">{user?.role}</span>
            <ThemeToggle />
          </div>
          {user?.role === 'system_admin' && (
            <NavLink
              to="/admin"
              className="mb-2 flex w-full items-center justify-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              Admin Panel
            </NavLink>
          )}
          <button
            onClick={signOut}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Sign Out
          </button>
        </div>
      </div>
    </aside>
  );
}
